import React, { useEffect, useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import MockedBadge from "@/components/MockedBadge";
import { Users2, Mail, UserPlus, ShieldCheck, Clock3, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "org_admin", label: "Org admin" },
  { value: "site_manager", label: "Site manager" },
  { value: "receptionist", label: "Receptionist" },
  { value: "host", label: "Host" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "host" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [u, i] = await Promise.all([api.get("/users"), api.get("/users/invitations")]);
      setUsers(u.data);
      setInvitations(i.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/invitations", form);
      toast.success("Invitation sent (MOCKED — view in Notifications log)");
      setForm({ email: "", name: "", role: "host" });
      setShowInvite(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="eb-overline">Team</div>
            <MockedBadge service="resend" label="Resend (email)" />
          </div>
          <h1 className="text-3xl font-semibold" data-testid="users-heading">Users & invitations</h1>
        </div>
        <button className="btn-accent" onClick={() => setShowInvite(!showInvite)} data-testid="invite-user-btn">
          <UserPlus className="w-4 h-4" /> Invite user
        </button>
      </div>

      {showInvite && (
        <form onSubmit={invite} className="eb-card p-6 mb-6 grid md:grid-cols-3 gap-3" data-testid="invite-form">
          <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="eb-input" data-testid="invite-name-input" />
          <input required type="email" placeholder="email@company.co.uk" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="eb-input" data-testid="invite-email-input" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="eb-input" data-testid="invite-role-select">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setShowInvite(false)}>Cancel</button>
            <button type="submit" disabled={saving} className="btn-accent" data-testid="invite-submit-btn">
              {saving ? "Sending…" : "Send invitation"}
            </button>
          </div>
        </form>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 eb-card overflow-hidden">
          <div className="px-5 py-4 border-b border-navy-100 flex items-center gap-2">
            <Users2 className="w-4 h-4 text-navy-600" />
            <h2 className="font-semibold">Active users ({users.length})</h2>
          </div>
          {loading ? (
            <p className="p-5 text-navy-500 text-sm">Loading…</p>
          ) : (
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-surface text-xs uppercase tracking-wider text-navy-500">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Role</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-navy-100 hover:bg-surface/60" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 font-medium">{u.name}</td>
                    <td className="px-5 py-3 text-navy-600">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-navy-700 bg-navy-100 px-2 py-0.5 rounded">
                        <ShieldCheck className="w-3 h-3" />
                        {u.role.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {u.email_verified ? (
                        <span className="inline-flex items-center gap-1 text-xs text-success"><CheckCircle2 className="w-3 h-3" /> Active</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-warning"><Clock3 className="w-3 h-3" /> Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan="4" className="px-5 py-8 text-center text-navy-500">No users yet.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="lg:col-span-2 eb-card overflow-hidden">
          <div className="px-5 py-4 border-b border-navy-100 flex items-center gap-2">
            <Mail className="w-4 h-4 text-navy-600" />
            <h2 className="font-semibold">Pending invitations ({invitations.filter((i) => i.status === "pending").length})</h2>
          </div>
          <div className="p-2" data-testid="invitations-list">
            {invitations.length === 0 && (
              <p className="text-sm text-navy-500 p-4">No invitations yet.</p>
            )}
            {invitations.map((inv) => (
              <div key={inv.id} className="p-3 rounded-lg hover:bg-surface flex items-start gap-3" data-testid={`invitation-${inv.id}`}>
                <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{inv.email}</div>
                  <div className="text-xs text-navy-500 capitalize">{inv.role.replace("_", " ")} · {inv.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}
